import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Image } from 'lucide-react';
import { blogApi } from '../api/cms';
import { defaultArticles } from '../api/defaults';
import type { BlogArticle } from '../types';
import { RichEditor, MediaPickerModal } from '../components/RichEditor';

const empty: Omit<BlogArticle, 'id' | 'createdAt' | 'updatedAt'> = {
  title: '', slug: '', excerpt: '', content: '', category: 'general',
  image: '', author: 'Interpersonal IT', status: 'draft',
  seo: { title: '', description: '', keywords: '', ogImage: '' },
};
const CATEGORIES = ['general', 'web-design', 'hosting', 'voip', 'call-center', 'crm', 'hardware', 'tech'];

function slugify(t: string) {
  return t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function BlogEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...empty, seo: { ...empty.seo } });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'content' | 'seo'>('content');
  const [showMedia, setShowMedia] = useState(false);
  const [mediaTarget, setMediaTarget] = useState<'cover' | 'insert'>('cover');
  // Key to force RichEditor remount on content change from media
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => {
    if (!isNew) {
      blogApi.get(id!).then(a => setForm({
        title: a.title, slug: a.slug, excerpt: a.excerpt, content: a.content,
        category: a.category, image: a.image, author: a.author, status: a.status,
        seo: { ...a.seo },
      })).catch(() => {
        const a = defaultArticles.find(x => x.id === id);
        if (a) setForm({ title: a.title, slug: a.slug, excerpt: a.excerpt, content: a.content, category: a.category, image: a.image, author: a.author, status: a.status, seo: { ...a.seo } });
      });
    }
  }, [id, isNew]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const setSeo = (k: string, v: string) => setForm(f => ({ ...f, seo: { ...f.seo, [k]: v } }));

  const handleTitleChange = (v: string) => {
    set('title', v);
    if (isNew) set('slug', slugify(v));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await blogApi.create(form);
      else await blogApi.update(id!, form);
      navigate('/blog');
    } catch { setSaving(false); }
  };

  const handleMediaSelect = (url: string) => {
    if (mediaTarget === 'cover') {
      set('image', url);
    } else {
      const imgHtml = `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:12px 0;" />`;
      setForm(f => ({ ...f, content: f.content + imgHtml }));
      setEditorKey(k => k + 1);
    }
    setShowMedia(false);
  };

  const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input {...props} className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
  );
  const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
    <textarea {...props} className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors resize-none" />
  );
  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div><label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">{label}</label>{children}</div>
  );

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/blog')} className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-all"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{isNew ? 'Articol nou' : 'Editare articol'}</h1>
        </div>
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
          <Save size={15} /> {saving ? 'Se salvează...' : 'Salvează'}
        </button>
      </div>

      <div className="flex border-b border-white/10">
        {(['content', 'seo'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'text-[#4ab8ff] border-[#295CA8]' : 'text-white/40 border-transparent hover:text-white'}`}>
            {t === 'content' ? '📝 Conținut' : '🔍 SEO'}
          </button>
        ))}
      </div>

      {tab === 'content' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Field label="Titlu articol">
              <Input value={form.title} onChange={e => handleTitleChange(e.target.value)} placeholder="Titlul articolului..." />
            </Field>
            <Field label="Slug URL">
              <div className="flex">
                <span className="px-3 py-2.5 bg-white/5 border border-r-0 border-white/10 rounded-l-lg text-xs text-white/30">/blog/</span>
                <input value={form.slug} onChange={e => set('slug', e.target.value)}
                  className="flex-1 bg-[#0d1117] border border-white/10 rounded-r-lg px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
              </div>
            </Field>
            <Field label="Rezumat (excerpt)">
              <Textarea value={form.excerpt} onChange={e => set('excerpt', e.target.value)} rows={3} placeholder="Scurtă descriere..." />
            </Field>
            <Field label="Conținut articol">
              <RichEditor
                key={editorKey}
                value={form.content}
                onChange={v => set('content', v)}
                onInsertImageFromLibrary={() => { setMediaTarget('insert'); setShowMedia(true); }}
                minHeight={320}
                placeholder="Începe să scrii conținutul articolului..."
              />
            </Field>
          </div>

          <div className="space-y-4">
            <Field label="Status">
              <select value={form.status} onChange={e => set('status', e.target.value as 'draft' | 'published')}
                className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors [&>option]:bg-[#0d1117] [&>option]:text-white">
                <option value="draft">📄 Draft</option>
                <option value="published">✅ Publicat</option>
              </select>
            </Field>
            <Field label="Categorie">
              <select value={form.category} onChange={e => set('category', e.target.value)}
                className="w-full bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors [&>option]:bg-[#0d1117] [&>option]:text-white">
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace('-', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Autor">
              <Input value={form.author} onChange={e => set('author', e.target.value)} />
            </Field>

            {/* Cover image */}
            <div>
              <label className="block text-xs text-white/50 mb-1.5 font-medium uppercase tracking-wide">Imagine cover</label>
              {form.image ? (
                <div className="relative rounded-xl overflow-hidden border border-white/10 group">
                  <img src={form.image} alt="Cover" className="w-full h-36 object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => { setMediaTarget('cover'); setShowMedia(true); }}
                      className="bg-[#295CA8] text-white text-xs px-3 py-1.5 rounded-lg">🔄 Schimbă</button>
                    <button onClick={() => set('image', '')}
                      className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg">🗑 Șterge</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setMediaTarget('cover'); setShowMedia(true); }}
                  className="w-full h-32 border-2 border-dashed border-white/15 rounded-xl flex flex-col items-center justify-center text-white/30 hover:border-[#295CA8]/60 hover:text-white/60 transition-all">
                  <Image size={24} className="mb-1" />
                  <span className="text-xs">Alege din Librărie</span>
                </button>
              )}
              <Input value={form.image} onChange={e => set('image', e.target.value)} placeholder="sau URL direct..." />
            </div>

            {/* Stats */}
            <div className="rounded-xl border border-white/8 p-3 space-y-1.5 text-xs text-white/35 bg-white/[0.015]">
              <div className="flex justify-between"><span>Cuvinte</span><span className="text-white/60">{form.content.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(Boolean).length}</span></div>
              <div className="flex justify-between"><span>Timp citire</span><span className="text-white/60">~{Math.ceil(form.content.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(Boolean).length / 200)} min</span></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-4xl">
          <Field label="Meta Title (max 60)">
            <Input value={form.seo.title} onChange={e => setSeo('title', e.target.value)} maxLength={60} placeholder="Titlu SEO..." />
            <span className={`text-xs mt-1 block ${form.seo.title.length > 55 ? 'text-yellow-400' : 'text-white/30'}`}>{form.seo.title.length}/60</span>
          </Field>
          <Field label="OG Image URL">
            <Input value={form.seo.ogImage} onChange={e => setSeo('ogImage', e.target.value)} placeholder="https://..." />
            {!form.seo.ogImage && form.image && (
              <button onClick={() => setSeo('ogImage', form.image)} className="text-xs text-[#4ab8ff] hover:underline mt-1">← Folosește imaginea cover</button>
            )}
          </Field>
          <Field label="Meta Description (max 160)">
            <Textarea value={form.seo.description} onChange={e => setSeo('description', e.target.value)} rows={3} maxLength={160} placeholder="Descriere SEO..." />
            <span className={`text-xs mt-1 block ${form.seo.description.length > 150 ? 'text-yellow-400' : 'text-white/30'}`}>{form.seo.description.length}/160</span>
          </Field>
          <Field label="Keywords">
            <Textarea value={form.seo.keywords} onChange={e => setSeo('keywords', e.target.value)} rows={3} placeholder="keyword1, keyword2" />
          </Field>
        </div>
      )}

      {showMedia && <MediaPickerModal onSelect={handleMediaSelect} onClose={() => setShowMedia(false)} />}
    </div>
  );
}
